import React, { useMemo, useContext, memo, useState } from 'react';
import { isUserOnline } from '../utils';
import { useAuth } from '../AuthContext';
import { PageContext } from '../PageContext';
import { Page, UserProfile } from '../types';
import BackButton from '../components/BackButton';
import LeaderboardAdCard from '../components/LeaderboardAdCard';

interface LeaderboardItemProps {
    user: UserProfile;
    rank: number;
    isCurrentUser: boolean;
    animationDelay: string;
}

const LeaderboardItem: React.FC<LeaderboardItemProps> = memo(({ user, rank, isCurrentUser, animationDelay }) => {
    const pageContext = useContext(PageContext);
    const handleProfileClick = () => pageContext?.switchPage(Page.Profile, user.displayName, { userId: user.uid });
    const score = user.score;
    
    return (
        <div onClick={handleProfileClick} style={{ animationDelay }} className={`animate-list-fade-in flex items-center p-3 rounded-lg shadow-sm cursor-pointer ${isCurrentUser ? 'bg-purple-100' : 'bg-white'}`}>
            <span className="font-bold text-lg w-8">{rank}</span>
            <div className="relative flex-shrink-0 mx-4">
                <img src={user.photoURL} alt={user.displayName} className="w-12 h-12 rounded-full object-cover" />
                {isUserOnline(user) && (
                    <span className="absolute bottom-0 right-0 block h-3.5 w-3.5 rounded-full bg-green-500 border-2 border-white" title="Online"></span>
                )}
            </div>
            <p className="flex-1 font-bold truncate">{user.displayName}</p>
            <p className="font-bold text-lg text-[var(--primary-dark-purple)]">{score} pts</p>
        </div>
    );
});

type LeaderboardFilter = 'world' | 'friends';

const LeaderboardPage: React.FC = () => {
    const { userProfile, allUsers } = useAuth();
    const [activeFilter, setActiveFilter] = useState<LeaderboardFilter>('world');

    const sortedUsers = useMemo(() => {
        if (!userProfile) return [];

        let filteredList: UserProfile[];

        switch (activeFilter) {
            case 'friends':
                // Friends are mutual follows
                const friendIds = userProfile.following.filter(id => userProfile.followers.includes(id));
                filteredList = allUsers.filter(u => friendIds.includes(u.uid));
                // Add self to the list to see own rank among friends
                if (!filteredList.some(u => u.uid === userProfile.uid)) {
                    filteredList.push(userProfile);
                }
                break;
            case 'world':
            default:
                // World: all non-blocked users
                filteredList = allUsers.filter(user => {
                    const iHaveBlockedThem = userProfile.blockedUsers?.includes(user.uid);
                    const theyHaveBlockedMe = user.blockedUsers?.includes(userProfile.uid);
                    return !iHaveBlockedThem && !theyHaveBlockedMe;
                });
                break;
        }

        return filteredList.sort((a, b) => b.score - a.score);
    }, [allUsers, userProfile, activeFilter]);

    const activeTabClasses = "bg-white text-[var(--primary-dark-purple)]";
    const inactiveTabClasses = "bg-transparent text-white";

    return (
        <div className="space-y-4">
            <BackButton />
            <h2 className="text-2xl font-bold">
                <span className="aurora-text">Leaderboard</span>
            </h2>

            {/* Filter Buttons */}
            <div className="p-1 rounded-full flex space-x-1" style={{ backgroundColor: 'var(--primary-dark-purple)'}}>
                <button onClick={() => setActiveFilter('world')} className={`flex-1 py-2 font-bold rounded-full transition-colors ${activeFilter === 'world' ? activeTabClasses : inactiveTabClasses}`}>World</button>
                <button onClick={() => setActiveFilter('friends')} className={`flex-1 py-2 font-bold rounded-full transition-colors ${activeFilter === 'friends' ? activeTabClasses : inactiveTabClasses}`}>Friends</button>
            </div>

            <div className="space-y-2">
                {sortedUsers.length > 0 ? (
                    sortedUsers.map((user, index) => (
                        <LeaderboardItem
                            key={user.uid}
                            user={user}
                            rank={index + 1}
                            isCurrentUser={user.uid === userProfile?.uid}
                            animationDelay={`${0.1 + index * 0.05}s`}
                        />
                    ))
                ) : (
                    <p className="text-center text-gray-500 py-8">No users to display in this category.</p>
                )}
            </div>
        </div>
    );
};

export default LeaderboardPage;